import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CmpInvoiceActionTypeEnum,
  CmpInvoiceDetailFieldsFragment,
  CmpInvoiceKindEnum,
  CmpOutboundEmailKindEnum,
  CmpOutboundEmailStatusEnum,
  GetUserQuery,
  useGetCmpInvoiceDetailQuery,
} from "../../../../graphql/types";
import {
  InvoiceDocumentMode,
  InvoicePreviewPendingAction,
  isAwaitingPaymentInvoiceDisplayState,
  isOverdueInvoiceDisplayState,
  isSavedSuccessfulInvoicePayment,
} from "../invoiceFormatting";
import { getValidInvoiceEmailRecipient } from "../invoiceDeliveryFeedback";
import {
  InvoiceDocumentRow,
  InvoicePreviewModal,
  printInvoicePreviewDocument,
} from "./ManageInvoiceActionModals";
import {
  getManageInvoiceDetailVariables,
  ManageInvoiceDetailVariables,
} from "./manageInvoiceDetailGate";
import { invoiceReadApolloContext } from "./usePagedManageInvoices";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
} from "../../../../utils/userFacingError";

type ChamberInfo = GetUserQuery["getUser"]["chamberInfo"];

export type SavedEventInvoiceDocumentModalProps = {
  actionLoading?: boolean;
  chamber?: ChamberInfo;
  chamberId: string;
  customerName: string;
  documentMode: InvoiceDocumentMode;
  invoiceId: string;
  // Fallback only: the saved invoice detail supplies the real kind. Reports
  // also open membership, advertising, and custom invoices here.
  invoiceKind: CmpInvoiceKindEnum;
  invoiceNumber: string;
  onAddPayment?: () => Promise<void> | void;
  onClose: () => void;
  onEmail?: (documentMode: InvoiceDocumentMode) => Promise<void> | void;
  onPendingAction?: () => Promise<void> | void;
  onPrintPdf?: (documentMode: InvoiceDocumentMode) => Promise<void> | void;
  open: boolean;
  paymentActionUnavailableReason?: string | null;
  pendingAction?: InvoicePreviewPendingAction;
  recipientEmail?: string | null;
  sourceEmailPermission: boolean;
};

export const getSavedEventDocumentName = (eventNameSnapshot?: string | null) =>
  eventNameSnapshot?.trim() || "Event name unavailable";

export const getSavedEventPaymentUnavailableMessage = (
  reason?: string | null,
): string | null =>
  reason?.trim()
    ? `You cannot take a payment on this invoice. ${reason.trim()}`
    : null;

export const getSavedEventInvoiceDetailVariables = ({
  chamberId,
  invoiceId,
}: {
  chamberId?: string | null;
  invoiceId?: string | null;
}): ManageInvoiceDetailVariables | null =>
  getManageInvoiceDetailVariables({ chamberId, invoiceId });

export const canEmailSavedEventDocument = ({
  documentMode,
  hasEmailCallback,
  payments,
  recipientEmail,
  sourceEmailPermission,
}: {
  documentMode: InvoiceDocumentMode;
  hasEmailCallback: boolean;
  payments: CmpInvoiceDetailFieldsFragment["payments"];
  recipientEmail?: string | null;
  sourceEmailPermission: boolean;
}) => {
  if (
    !sourceEmailPermission ||
    !hasEmailCallback ||
    !getValidInvoiceEmailRecipient(recipientEmail)
  ) {
    return false;
  }

  return (
    documentMode !== "PAYMENT_RECEIPT" ||
    payments.some(isSavedSuccessfulInvoicePayment)
  );
};

export const isSavedEventInvoiceResend = ({
  lastPaymentRequestSentAtUtcMilli,
  outboundEmails,
}: {
  lastPaymentRequestSentAtUtcMilli?: number | null;
  outboundEmails: CmpInvoiceDetailFieldsFragment["outboundEmails"];
}): boolean =>
  (Number.isFinite(lastPaymentRequestSentAtUtcMilli) &&
    Number(lastPaymentRequestSentAtUtcMilli) > 0) ||
  outboundEmails.some(
    ({ kind, status }) =>
      status === CmpOutboundEmailStatusEnum.Sent &&
      (kind === CmpOutboundEmailKindEnum.InvoiceDocument ||
        kind === CmpOutboundEmailKindEnum.InvoicePaymentRequest),
  );

export const canApplySavedEventInvoicePayment = ({
  amountDueInCents,
  displayState,
  hasAddPaymentCallback,
}: {
  amountDueInCents: number;
  displayState: CmpInvoiceDetailFieldsFragment["displayState"];
  hasAddPaymentCallback: boolean;
}) => {
  if (
    !hasAddPaymentCallback ||
    amountDueInCents <= 0 ||
    (!isAwaitingPaymentInvoiceDisplayState(displayState) &&
      !isOverdueInvoiceDisplayState(displayState))
  ) {
    return false;
  }

  // The callback is supplied only by the linked Event Sales page after it
  // verifies the source-owned payment rules and reconciliation state. Generic
  // invoice payment actions are intentionally unavailable for event invoices,
  // so they must not hide this separate source-owned action.
  return true;
};

export const canSendSavedEventInvoiceDocument = ({
  canEmailDocument,
  displayState,
  documentMode,
}: {
  canEmailDocument: boolean;
  displayState: CmpInvoiceDetailFieldsFragment["displayState"];
  documentMode: InvoiceDocumentMode;
}) =>
  documentMode === "INVOICE" &&
  canEmailDocument &&
  (isAwaitingPaymentInvoiceDisplayState(displayState) ||
    isOverdueInvoiceDisplayState(displayState));

type InvoiceDetailWithEventSnapshot = CmpInvoiceDetailFieldsFragment & {
  invoice: CmpInvoiceDetailFieldsFragment["invoice"] & {
    eventNameSnapshot?: string | null;
    eventStartAtUtcMilliSnapshot?: number | null;
    eventTimeZoneSnapshot?: string | null;
  };
};

/**
 * Saved event document preview for ticket and sponsorship rows. Draft previews
 * stay in the event create routes and do not use this component.
 */
const SavedEventInvoiceDocumentModal = ({
  actionLoading = false,
  chamber,
  chamberId,
  customerName,
  documentMode,
  invoiceId,
  invoiceKind,
  invoiceNumber,
  onAddPayment,
  onClose,
  onEmail,
  onPendingAction,
  onPrintPdf,
  open,
  paymentActionUnavailableReason,
  pendingAction,
  recipientEmail,
  sourceEmailPermission,
}: SavedEventInvoiceDocumentModalProps) => {
  const [localActionError, setLocalActionError] = useState<string | null>(null);
  const [localActionLoading, setLocalActionLoading] = useState(false);
  const actionInFlightRef = useRef(false);
  const detailVariables = getSavedEventInvoiceDetailVariables({
    chamberId,
    invoiceId,
  });
  const { data, error, loading } = useGetCmpInvoiceDetailQuery({
    // A failed read shows in the preview, not the full-page error.
    context: invoiceReadApolloContext,
    fetchPolicy: "cache-and-network",
    skip: !open || !detailVariables,
    variables:
      detailVariables ||
      ({
        input: {
          chamberId: "CMP_SKIPPED",
          invoiceId: "CMP_SKIPPED",
        },
      } as const),
  });
  const savedDetail = data?.getCmpInvoiceDetail || null;
  const detail = useMemo<InvoiceDetailWithEventSnapshot | null>(() => {
    if (!savedDetail) {
      return null;
    }

    const savedEventName = (
      savedDetail.invoice as InvoiceDetailWithEventSnapshot["invoice"]
    ).eventNameSnapshot?.trim();
    return {
      ...savedDetail,
      invoice: {
        ...savedDetail.invoice,
        eventNameSnapshot: getSavedEventDocumentName(savedEventName),
      },
    };
  }, [savedDetail]);

  const row = useMemo<InvoiceDocumentRow | null>(() => {
    if (!detail) {
      return null;
    }

    const latestPayment = [...detail.payments]
      .filter(isSavedSuccessfulInvoicePayment)
      .sort(
        (first, second) => second.receivedAtUtcMilli - first.receivedAtUtcMilli,
      )[0];

    return {
      availableActions: detail.availableActions,
      balanceDueInCents: detail.invoice.amountDueInCents,
      customerName:
        detail.billingSnapshot?.customerName ||
        detail.invoice.billingSnapshot?.customerName ||
        customerName,
      daysPastDue: detail.daysPastDue,
      displayState: detail.displayState,
      dueAtUtcMilli: detail.invoice.dueAtUtcMilli,
      id: detail.invoice.id,
      invoiceKind: detail.invoice.invoiceKind || invoiceKind,
      invoiceNumber: detail.invoice.invoiceNumber || invoiceNumber,
      issuedAtUtcMilli: detail.invoice.issuedAtUtcMilli,
      payerDataWarning: detail.payerDataWarning,
      paymentMethodLabel:
        latestPayment?.paymentMethodLabel ||
        detail.invoice.paymentChoice.manualPaymentMethodLabel ||
        "",
      paymentPlan: detail.invoice.paymentPlan,
      paymentRules: detail.invoice.paymentRules || undefined,
      sourceLabel: "",
      totalAmountInCents: detail.invoice.totalAmountInCents || 0,
    };
  }, [customerName, detail, invoiceKind, invoiceNumber]);

  const canEmailDocument = detail
    ? canEmailSavedEventDocument({
        documentMode,
        hasEmailCallback: Boolean(onEmail),
        payments: detail.payments,
        recipientEmail,
        sourceEmailPermission,
      })
    : false;
  const canPrintDocument = Boolean(
    detail?.availableActions.some(
      ({ action, available }) =>
        action === CmpInvoiceActionTypeEnum.DownloadPdf && available,
    ) &&
    (documentMode !== "PAYMENT_RECEIPT" ||
      detail.payments.some(isSavedSuccessfulInvoicePayment)),
  );
  const canApplyPayment = detail
      ? canApplySavedEventInvoicePayment({
        amountDueInCents: detail.invoice.amountDueInCents,
        displayState: detail.displayState,
        hasAddPaymentCallback: Boolean(onAddPayment),
      })
    : false;
  const canSendInvoice = detail
    ? canSendSavedEventInvoiceDocument({
        canEmailDocument,
        displayState: detail.displayState,
        documentMode,
      })
    : false;

  useEffect(() => {
    if (open) {
      setLocalActionError(null);
      setLocalActionLoading(false);
      actionInFlightRef.current = false;
    }
  }, [invoiceId, open]);

  const runDocumentAction = async (
    action: (() => Promise<void> | void) | undefined,
  ) => {
    if (!action || actionLoading || actionInFlightRef.current) {
      return;
    }

    actionInFlightRef.current = true;
    setLocalActionError(null);
    setLocalActionLoading(true);
    try {
      await action();
    } catch (actionFailure) {
      setLocalActionError(
        getUserFacingErrorText(
          actionFailure,
          buildStaffErrorFallback("finish this invoice action"),
        ),
      );
    } finally {
      actionInFlightRef.current = false;
      setLocalActionLoading(false);
    }
  };

  return (
    <InvoicePreviewModal
      actionLoading={actionLoading || localActionLoading}
      canApplyPayment={canApplyPayment}
      canEmailDocument={documentMode !== "PAYMENT_RECEIPT" && canEmailDocument}
      canPrintPdf={
        canPrintDocument &&
        (documentMode === "PAYMENT_RECEIPT" || Boolean(onPrintPdf))
      }
      canSendInvoice={canSendInvoice}
      canSendReceipt={documentMode === "PAYMENT_RECEIPT" && canEmailDocument}
      chamber={chamber}
      detail={detail}
      documentMode={documentMode}
      errorMessage={
        localActionError ||
        (error
          ? getUserFacingErrorText(
              error,
              buildStaffErrorFallback("load this invoice"),
            )
          : null) ||
        getSavedEventPaymentUnavailableMessage(
          paymentActionUnavailableReason,
        )
      }
      loading={loading}
      onAddPayment={() => void runDocumentAction(onAddPayment)}
      onClose={onClose}
      onEmailDocument={() =>
        void runDocumentAction(
          onEmail ? () => onEmail(documentMode) : undefined,
        )
      }
      onPendingAction={
        onPendingAction
          ? () => void runDocumentAction(onPendingAction)
          : undefined
      }
      onPrintPdf={() => {
        if (documentMode === "PAYMENT_RECEIPT") {
          printInvoicePreviewDocument();
          return;
        }

        void runDocumentAction(
          onPrintPdf ? () => onPrintPdf(documentMode) : undefined,
        );
      }}
      onSendInvoice={() =>
        void runDocumentAction(
          onEmail ? () => onEmail(documentMode) : undefined,
        )
      }
      onSendReceipt={() =>
        void runDocumentAction(
          onEmail ? () => onEmail(documentMode) : undefined,
        )
      }
      open={open}
      pendingAction={pendingAction}
      row={row}
      sendIsResend={Boolean(
        detail &&
        isSavedEventInvoiceResend({
          lastPaymentRequestSentAtUtcMilli:
            detail.invoice.lastPaymentRequestSentAtUtcMilli,
          outboundEmails: detail.outboundEmails,
        }),
      )}
    />
  );
};

export default SavedEventInvoiceDocumentModal;
